import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { EyeIcon, EyeSlashIcon, CheckCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { guardianApi } from '../../services/api/guardian';
import { GuardianInvitationAccept } from '../../types/api';
import { Card, H1, Text, Badge, Button, Spinner } from '../../components/ui';
import { Input } from '../../components/forms';
import { useToast } from '../../components/ui';

const acceptSchema = z.object({
  first_name: z.string().min(1, 'First name is required'),
  last_name: z.string().min(1, 'Last name is required'),
  phone: z.string().min(1, 'Phone number is required'),
  password: z.string().min(8, 'Password must be at least 8 characters'),
  confirm_password: z.string(),
  id_number: z.string().optional(),
  address: z.string().optional(),
}).refine(data => data.password === data.confirm_password, {
  message: 'Passwords do not match',
  path: ['confirm_password'],
});

type AcceptFormData = z.infer<typeof acceptSchema>;

export const GuardianInvitation: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const navigate = useNavigate();
  const { addToast } = useToast();
  const [showPassword, setShowPassword] = useState(false);
  const [accepted, setAccepted] = useState(false);

  const { data: invitation, isLoading, error } = useQuery({
    queryKey: ['guardian-invitation', token],
    queryFn: () => guardianApi.invitations.verify(token),
    enabled: !!token,
    retry: false,
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<AcceptFormData>({
    resolver: zodResolver(acceptSchema),
  });

  const acceptMutation = useMutation({
    mutationFn: (data: AcceptFormData) => {
      const { confirm_password: _confirm_password, ...acceptData } = data;
      return guardianApi.invitations.accept(token, { ...acceptData, token } as GuardianInvitationAccept);
    },
    onSuccess: () => {
      setAccepted(true);
    },
    onError: () => {
      addToast({ title: 'Error', message: 'Failed to accept invitation', type: 'error' });
    },
  });

  if (!token) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 to-primary-100 flex items-center justify-center px-4">
        <Card className="max-w-md w-full p-8 text-center">
          <ExclamationTriangleIcon className="mx-auto h-16 w-16 text-yellow-500 mb-4" />
          <H1 className="mb-2">Invalid Link</H1>
          <Text className="text-gray-600">
            This invitation link is missing a token. Please check the link from your email.
          </Text>
        </Card>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 to-primary-100 flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error || !invitation) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 to-primary-100 flex items-center justify-center px-4">
        <Card className="max-w-md w-full p-8 text-center">
          <ExclamationTriangleIcon className="mx-auto h-16 w-16 text-red-500 mb-4" />
          <H1 className="mb-2">Invalid Invitation</H1>
          <Text className="text-gray-600 mb-4">
            This invitation link is invalid or has expired. Please contact the school to request a new invitation.
          </Text>
          <Button variant="outline" onClick={() => navigate('/guardian/login')}>
            Go to Login
          </Button>
        </Card>
      </div>
    );
  }

  if (invitation.is_used || accepted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 to-primary-100 flex items-center justify-center px-4">
        <Card className="max-w-md w-full p-8 text-center">
          <CheckCircleIcon className="mx-auto h-16 w-16 text-green-500 mb-4" />
          <H1 className="mb-2">{accepted ? 'Welcome!' : 'Already Accepted'}</H1>
          <Text className="text-gray-600 mb-6">
            {accepted
              ? 'Your account has been created. You can now log in to view your child\'s progress.'
              : 'This invitation has already been accepted. Please log in to continue.'}
          </Text>
          <Button variant="primary" onClick={() => navigate('/guardian/login')}>
            Go to Login
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-primary-100 flex items-center justify-center px-4 py-8">
      <Card className="max-w-lg w-full p-8">
        <H1 className="text-center mb-2">Accept Invitation</H1>
        <Text className="text-center text-gray-600 mb-6">
          You've been invited as a <Badge variant="primary">{invitation.relationship}</Badge> to access your child's records.
        </Text>

        <div className="bg-gray-50 rounded-lg p-4 mb-6">
          <Text className="text-sm text-gray-500">Invitation for</Text>
          <Text className="font-medium">{invitation.email}</Text>
        </div>

        <form onSubmit={handleSubmit((data) => acceptMutation.mutate(data))} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Input
              {...register('first_name')}
              label="First Name"
              error={errors.first_name?.message}
            />
            <Input
              {...register('last_name')}
              label="Last Name"
              error={errors.last_name?.message}
            />
          </div>
          <Input
            {...register('phone')}
            label="Phone Number"
            placeholder="e.g. 082 123 4567"
            error={errors.phone?.message}
          />
          <Input
            {...register('id_number')}
            label="ID Number (Optional)"
            placeholder="SA ID or passport number"
            error={errors.id_number?.message}
          />
          <Input
            {...register('address')}
            label="Address (Optional)"
            error={errors.address?.message}
          />
          <div className="relative">
            <Input
              {...register('password')}
              label="Create Password"
              type={showPassword ? 'text' : 'password'}
              error={errors.password?.message}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-[38px] text-gray-500 hover:text-gray-700"
              tabIndex={-1}
            >
              {showPassword ? <EyeSlashIcon className="w-5 h-5" /> : <EyeIcon className="w-5 h-5" />}
            </button>
          </div>
          <Input
            {...register('confirm_password')}
            label="Confirm Password"
            type="password"
            error={errors.confirm_password?.message}
          />

          <Button
            type="submit"
            variant="primary"
            fullWidth
            isLoading={acceptMutation.isPending}
          >
            Create Account
          </Button>
        </form>
      </Card>
    </div>
  );
};
